/**
 * MyPermissionsSection - секция «Мои права» (только просмотр).
 *
 * Права роли текущего пользователя через /api/v2/profile/permissions: пользователь и роль определяются сервером
 * по сессии, в списке только выданные права, сгруппированные по модулям матрицы. Зона видимости подписывается так же,
 * как в конструкторе ролей (PERMISSION_SCOPES_CONFIG), а значение вне вариантов права - общим названием зоны.
 * Ранг 100 - полный доступ без ограничений, зона не указывается.
 */
import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Paper,
  Typography,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  TextField,
  InputAdornment,
  Chip,
  Alert,
  Button,
  CircularProgress
} from '@mui/material';
import { VerifiedUserOutlined, ExpandMore, Search } from '@mui/icons-material';
import SectionHeaderDivider from './SectionHeaderDivider';
import { apiErrorMessage } from '../../admin/reports/edit/editApi';
import { PERMISSION_SCOPES_CONFIG } from '../../admin/RoleManagement/scopes';
import { fetchMyPermissions, MyPermissions, MyPermission } from '../profileApi';

// Значения, которых нет среди вариантов права: старые записи первой миграции ролей (self у кабинета аптеки)
// и права, еще не описанные в конструкторе. Сотруднику нужно понятное название, а не системный код
const COMMON_SCOPE_LABELS: Record<string, string> = {
  all: 'Вся сеть',
  self: 'Только своя аптека',
  own_pharmacy: 'Только своя аптека',
  assigned_pharmacies: 'Курируемые аптеки',
  own_region: 'Свой регион',
  created_by_me: 'Созданные мной',
  my_documents: 'Мои документы',
  as_recipient: 'Я - получатель',
  below_my_role: 'Роли ниже моей',
  my_role_and_below: 'Моя роль и ниже',
  all_in_list: 'Все в списке',
  true: 'Разрешено'
};

const scopeLabel = (permission: MyPermission): string | null => {
  if (!permission.hasScope) return null;
  if (permission.scope === null) return 'Полный доступ';
  const options = PERMISSION_SCOPES_CONFIG[permission.code] || [];
  const option = options.filter(item => item.value === permission.scope)[0];
  if (option) return option.label;
  return COMMON_SCOPE_LABELS[permission.scope] || 'Особая зона';
};

const matches = (permission: MyPermission, query: string): boolean =>
  !query ||
  permission.name.toLowerCase().indexOf(query) >= 0 ||
  permission.code.toLowerCase().indexOf(query) >= 0 ||
  permission.description.toLowerCase().indexOf(query) >= 0;

const MyPermissionsSection: React.FC = () => {
  const [data, setData] = useState<MyPermissions | null>(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [query, setQuery] = useState('');
  const [expanded, setExpanded] = useState<string | false>(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      setData(await fetchMyPermissions());
      setLoadError(null);
    } catch (err) {
      setLoadError(apiErrorMessage(err, 'Не удалось загрузить права'));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const needle = query.trim().toLowerCase();
  const modules = data
    ? data.modules
      .map(module => ({ ...module, permissions: module.permissions.filter(permission => matches(permission, needle)) }))
      .filter(module => module.permissions.length > 0)
    : [];

  return (
    <Paper
      id="section-permissions"
      elevation={0}
      sx={{
        p: { xs: 2.5, sm: 3.5 },
        borderRadius: '20px',
        bgcolor: '#ffffff',
        border: '1px solid #e2e8f0',
        boxShadow: '0 2px 12px rgba(0, 0, 0, 0.03)',
        display: 'flex',
        flexDirection: 'column',
        gap: 3,
        scrollMarginTop: '24px'
      }}
    >
      <SectionHeaderDivider
        id="header-permissions"
        icon={<VerifiedUserOutlined sx={{ fontSize: 22 }} />}
        title="Мои права"
        subtitle="Какие действия разрешены в системе. Права настраивает администратор."
        badge={
          data ? (
            <Chip
              label={data.role.name}
              size="small"
              sx={{
                fontWeight: 600,
                fontSize: '0.75rem',
                borderRadius: '8px',
                bgcolor: data.role.badge.bg,
                color: data.role.badge.text,
                border: `1px solid ${data.role.badge.border}`
              }}
            />
          ) : undefined
        }
      />

      {loadError && (
        <Alert
          severity="error"
          sx={{ borderRadius: '12px' }}
          action={
            <Button color="inherit" size="small" onClick={load} sx={{ textTransform: 'none', fontWeight: 600 }}>
              Повторить
            </Button>
          }
        >
          {loadError}
        </Alert>
      )}

      {loading && !data && !loadError ? (
        <Box sx={{ py: 3, display: 'flex', justifyContent: 'center' }}>
          <CircularProgress size={28} />
        </Box>
      ) : data ? (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          {data.unlimited && (
            <Alert severity="info" sx={{ borderRadius: '12px' }}>
              Ранг {data.role.level}: полный доступ ко всем модулям и данным без ограничений по зоне видимости.
            </Alert>
          )}

          {data.total === 0 ? (
            <Typography variant="body2" sx={{ color: '#94a3b8', py: 1 }}>
              Вашей роли пока не выдано ни одного права
            </Typography>
          ) : (
            <>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
                <TextField
                  size="small"
                  value={query}
                  onChange={e => setQuery(e.target.value)}
                  placeholder="Поиск по названию или коду права"
                  sx={{ flex: '1 1 260px', maxWidth: 420 }}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <Search fontSize="small" sx={{ color: '#94a3b8' }} />
                      </InputAdornment>
                    )
                  }}
                />
                <Typography variant="body2" sx={{ color: '#64748b' }}>
                  Выдано прав: {data.total} в модулях: {data.modules.length}
                </Typography>
              </Box>

              {modules.length === 0 ? (
                <Typography variant="body2" sx={{ color: '#94a3b8', py: 1 }}>
                  Ничего не найдено
                </Typography>
              ) : (
                <Box sx={{ border: '1px solid #e2e8f0', borderRadius: '14px', overflow: 'hidden' }}>
                  {modules.map((module, index) => (
                    <Accordion
                      key={module.id}
                      disableGutters
                      elevation={0}
                      square
                      expanded={Boolean(needle) || expanded === module.id}
                      onChange={(_, open) => setExpanded(open ? module.id : false)}
                      sx={{ borderTop: index === 0 ? 'none' : '1px solid #e2e8f0', '&:before': { display: 'none' } }}
                    >
                      <AccordionSummary expandIcon={<ExpandMore />} sx={{ px: 2, '&:hover': { bgcolor: '#f8fafc' } }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                          <Typography sx={{ fontWeight: 700, color: '#0f172a', fontSize: '0.92rem' }}>{module.name}</Typography>
                          <Typography variant="caption" sx={{ color: '#64748b', fontWeight: 600 }}>
                            {module.permissions.length}
                          </Typography>
                        </Box>
                      </AccordionSummary>
                      <AccordionDetails sx={{ p: 0 }}>
                        {module.permissions.map(permission => {
                          const scope = scopeLabel(permission);
                          return (
                            <Box
                              key={permission.code}
                              sx={{
                                px: 2,
                                py: 1.25,
                                borderTop: '1px solid #f1f5f9',
                                display: 'flex',
                                flexWrap: 'wrap',
                                alignItems: 'flex-start',
                                justifyContent: 'space-between',
                                columnGap: 2,
                                rowGap: 0.75
                              }}
                            >
                              <Box sx={{ flex: '1 1 220px', minWidth: 0 }}>
                                <Typography variant="body2" sx={{ fontWeight: 600, color: '#0f172a', fontSize: '0.86rem' }}>
                                  {permission.name}
                                  {/* длинный код переносится по точкам и внутри слова, а не раздвигает строку */}
                                  <Typography
                                    component="span"
                                    sx={{ ml: 1, fontFamily: 'monospace', fontSize: '0.74rem', color: '#94a3b8', fontWeight: 400, overflowWrap: 'anywhere' }}
                                  >
                                    {permission.code}
                                  </Typography>
                                </Typography>
                                {permission.description && (
                                  <Typography variant="caption" sx={{ color: '#64748b', display: 'block', mt: 0.25 }}>
                                    {permission.description}
                                  </Typography>
                                )}
                              </Box>
                              {scope && (
                                <Chip
                                  label={scope}
                                  size="small"
                                  variant="outlined"
                                  sx={{ flexShrink: 0, fontWeight: 600, fontSize: '0.72rem', borderRadius: '6px', color: '#334155', borderColor: '#cbd5e1' }}
                                />
                              )}
                            </Box>
                          );
                        })}
                      </AccordionDetails>
                    </Accordion>
                  ))}
                </Box>
              )}
            </>
          )}
        </Box>
      ) : null}
    </Paper>
  );
};

export default MyPermissionsSection;
